"use client";

import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useDict } from "@/app/[lang]/providers/DictionaryProvider";
import { useLaporanTahunan } from "@/app/[lang]/investor-relations/annual-reports/useLaporanTahunan";
import { useSustainabilityReport } from "@/app/[lang]/sustainability/sustainability-report/useSustainabilityReport";
import { HomePage } from "../type/type";

import "swiper/css";
import "swiper/css/navigation";

export default function Report({ data }: { data: HomePage | undefined }) {
  const dict = useDict();
  const { data: annualData } = useLaporanTahunan();
  const { data: sustainabilityData } = useSustainabilityReport();

  const latestAnnual = annualData?.data[0];
  const latestSustainability = sustainabilityData?.data[0];

  const reports = [
    {
      id: 1,
      title: latestAnnual?.title ?? (dict.homepage?.annual_report || "Annual report"),
      description: data?.report_annual_desc || dict.homepage?.annual_report_desc || "This report highlights our year-long journey, key achievements, growth, and impact—reflecting our commitment to progress and excellence",
      image: latestAnnual?.thumbnail_path || "/images/annual.png",
      href: data?.report_annual_button_url || latestAnnual?.file_path || "#",
      buttonText: data?.report_annual_button_text || dict.homepage?.view_full_report || "View Full Report",
    },
    {
      id: 2,
      title: latestSustainability?.title ?? (dict.homepage?.sustainability_report || "Sustainability report"),
      description: data?.report_sustainability_desc || dict.homepage?.sustainability_report_desc || "This report summarizes our progress in creating long-term value through environmental, social, and governance efforts",
      image: latestSustainability?.thumbnail_path || "/images/sustainability.png",
      href: data?.report_sustainability_button_url || latestSustainability?.file_path || "#",
      buttonText: data?.report_sustainability_button_text || dict.homepage?.view_full_report || "View Full Report",
    },
  ];
  return (
    <div className="w-full bg-[url('/images/bg-report.png')] bg-no-repeat bg-cover">
      <div className="max-w-7xl mx-auto py-16 md:py-[120px] px-4 sm:px-6 relative">
        <div className="mb-8 md:mb-12 text-center md:text-left">
          <span className="text-sm text-negatif">{data?.report_label || "REPORT"}</span>
          <h2 className="font-bold text-3xl md:text-5xl text-negatif mt-1">
            {data?.report_title || dict.homepage?.report_title || "Stories Behind the Numbers"}
          </h2>
          {data?.report_desc
            ? <div className="text-negatif text-base leading-relaxed mt-6 max-w-2xl mx-auto md:mx-0" dangerouslySetInnerHTML={{ __html: data.report_desc }} />
            : <p className="text-negatif text-base leading-relaxed mt-6 max-w-2xl mx-auto md:mx-0">{dict.homepage?.report_subtitle || "Behind every number lies a story of challenges, achievements, and meaning"}</p>
          }
        </div>

        <div className="relative px-10 sm:px-12 md:px-0">
          {/* Custom Nav */}
          <button
            type="button"
            aria-label="Previous report"
            className="prev-btn cursor-pointer absolute left-0 md:-left-12 top-1/2 z-10 -translate-y-1/2 bg-transparent border-4 border-white rounded-full p-1 shadow"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </button>
          <button
            type="button"
            aria-label="Next report"
            className="next-btn cursor-pointer absolute right-0 md:-right-12 top-1/2 z-10 -translate-y-1/2 bg-transparent border-4 border-white rounded-full p-1 shadow"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-white" />
          </button>

          <Swiper
            modules={[Navigation]}
            spaceBetween={40}
            slidesPerView={1}
            breakpoints={{
              768: { slidesPerView: 2 },
            }}
            navigation={{
              prevEl: ".prev-btn",
              nextEl: ".next-btn",
            }}
            loop
          >
            {reports.map((report) => (
              <SwiperSlide key={report.id}>
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center sm:items-start bg-transparent text-center sm:text-left">
                  <div className="w-full max-w-[226px] aspect-[226/320] relative flex-shrink-0">
                    <Image
                      src={report.image}
                      alt={report.title}
                      fill
                      className="rounded-xl object-cover"
                    />
                  </div>

                  <div className="flex w-full min-w-0 flex-col items-center sm:items-start sm:h-[220px]">
                    <h3 className="text-white text-lg md:text-xl font-semibold ">
                      {report.title}
                    </h3>
                    <div className="border-t border-white w-full mt-4 sm:mt-14 mb-4 sm:mb-8"></div>
                    <div className="min-w-0">
                      <div className="text-white/80 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: report.description }} />
                      <a
                        href={report.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block mt-3 text-white underline underline-offset-4 hover:text-primary-300 text-sm font-medium"
                      >
                        {report.buttonText}
                      </a>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  );
}
